import React, { useEffect } from "react";
import { Crisp } from "crisp-sdk-web";
import mixpanel from "mixpanel-browser";
import "../styles/App.scss";

import ProtectedRoutes from "./ProtectedRoutes";
import logo from "../assets/logo-full-white.png";
import logoBlack from "../assets/logo-full.png";
import { API, Auth } from "../utils/amplifyWrapper";
import SignIn from "./auth/SignIn";
import SignUp from "./auth/SignUp";
import ForgotPassword from "./auth/ForgotPassword";
import GoogleCallback from "../components/GoogleCallback";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { useVerifyCheckout } from "../hooks/useVerifyCheckout";
import Spinner from "../components/Spinner";
import RouteSkeleton from "../components/skeletons/RouteSkeleton";
import BootLoadingScreen from "../components/BootLoadingScreen";
import { useAuthStore } from "../zustand-store/useAuthStore";
import { trackLoginOncePerDay } from "../services/mixpanel";
import { captureFirstTouch } from "../utils/FirstTouch";
import { captureSignupSource } from "../utils/SignupSource";
import PageViewTracker from "../components/PageViewTracker";
// import { UpgradePlanModalV2 } from "../components/plans/UpgradePlanModal";
// import { useDispatch } from "react-redux";
// import { addMixPanelEvent } from "../store/actions";
Crisp.configure(process.env.REACT_APP_CRISP_WEBSITE_ID, {
  autoload: false
});

const AUTH_STYLES = `
  @keyframes waveBar { from { transform:scaleY(.25); } to { transform:scaleY(1); } }
  @keyframes pulse { 0%,100% { transform:translate(-50%,-50%) scale(1); opacity:.6; } 50% { transform:translate(-50%,-50%) scale(1.08); opacity:1; } }
  @keyframes float { from { transform:translateY(0); } to { transform:translateY(-5px); } }
`;

const FEATURES = [
  'Start for free — no credit card required',
  '900+ premium voices in 140+ languages',
  'Unlimited previews and exports',
  'Full commercial rights',
  'Advanced editing studio',
];

// GROW-81: how a resumed session was originally authenticated, so retention can be
// sliced by method. SSO isn't offered, so it's Google (federated) vs email (native).
const deriveAuthMethod = (user) => {
  const identities = user?.attributes?.identities;
  if (identities) {
    try {
      const parsed = typeof identities === "string" ? JSON.parse(identities) : identities;
      const providers = Array.isArray(parsed) ? parsed : [parsed];
      if (providers.some((p) => p?.providerName === "Google")) return "google";
    } catch (_) { /* fall through to username check */ }
  }
  if (typeof user?.username === "string" && user.username.toLowerCase().startsWith("google")) return "google";
  return "email";
};

mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, {
  record_sessions_percent: 0,  // Don't record anyone by default
  record_mask_all_text: false,
  record_mask_all_inputs: false,
});

captureFirstTouch();
// before any redirect to Google sign-in, which drops the website's src_page / src_cta
captureSignupSource();

// Custom auth states (replacing removed @aws-amplify/ui-components)
const AuthState = {
  SignIn: "signin",
  SignUp: "signup", 
  SignedIn: "signedin",
  Onboarding: "onboarding",
  ForgotPassword: "forgotPassword"
};

function App() {
  let query = new URLSearchParams(
    (() => {
      try {
        return new URL(window.location.href).search;
      } catch {
        return "";
      }
    })()
  );
  const verifyingCheckout = useVerifyCheckout();
  const sessionRecordingStarted = React.useRef(false);
  // Includes /content/new — CreateContent.js also shows the studio loader while it
  // creates the blank content doc and redirects into the editor.
  const isOnStudioRoute = /\/organisation\/[^/]+\/integration\/[^/]+\/content\/[^/]+$/.test(window.location.pathname);

  let states = ["signin", "signup"];

  // const dispatch = useDispatch()
  const { authState, user, isCheckingAuth, setAuthState, setUser, setIsCheckingAuth } = useAuthStore();

  useEffect(() => {
    Crisp.load()

    // Skip auth check if we're on Google callback route
    if (window.location.pathname === '/google/callback') {
      setIsCheckingAuth(false);
      return;
    }

    // Check if we're on onboarding routes
    const isOnboardingRoute = window.location.pathname.startsWith('/onboarding');
    
    Auth.currentAuthenticatedUser({
      bypassCache: false,
    })
      .then((user) => {
        setUser(user);
        // If on onboarding route, set state to onboarding (not signedin)
        if (isOnboardingRoute) {
          setAuthState('onboarding');
          setIsCheckingAuth(false);
          return;
        }
        // GROW-81: a resolved session on app load = a returning user whose token was
        // restored silently (no credentials typed). This is the biggest gap in the Login
        // count. Deduped to once/day and skipped on onboarding so new users aren't counted.
        trackLoginOncePerDay({ method: deriveAuthMethod(user) });
        setAuthState("signedin");
        setIsCheckingAuth(false);
      })
      .catch((err) => {
        if (query.has("state") && states.includes(query.get("state"))) {
          setAuthState(query.get("state"));
          setIsCheckingAuth(false);
          return;
        }

        setAuthState("signin");
        setIsCheckingAuth(false);
      });

  }, []);

  useEffect(() => {
    if (user) {

      // Set User Details on Crisp
      if (user?.attributes?.email && (user?.attributes?.given_name || user?.attributes?.family_name)) {
        Crisp.user.setEmail(user.attributes.email);
        Crisp.user.setNickname([user.attributes.given_name, user.attributes.family_name].join(" "));
      }

      API.get("main", "/organisation").then((response) => {
        if (response.data.length > 0) {
          const org = response.data[0];
          const orgId = org.PK.split("#")[1];

          window.hj('identify', orgId, {
            orgId: orgId
          });

          mixpanel.identify(user?.username);
          mixpanel.people.set({
            $name: [user?.attributes?.given_name, user?.attributes?.family_name].filter(Boolean).join(" "),
            $email: user?.attributes?.email,
            orgId: orgId,
            plan: org.plan_id,
          });
          // Start session recording only once per page load, after the user
          // is identified — so every replay has name, email, orgId and plan attached.
          if (!sessionRecordingStarted.current) {
            sessionRecordingStarted.current = true;
            mixpanel.start_session_recording();
          }

          // Set up Crisp if needed
          API.get("main", `/organisation/${orgId}`).then((response) => {
            const gates = response.data.gates;
            if (gates.can_access_chat_support) {
              Crisp.chat.show()
            }
          }).catch((error) => {
            console.error('Error fetching org details for Crisp:', error);
          });
        } else {
          // Authenticated user but no organization - redirect to signup for recovery
          console.log('❌ NO ORGS FOUND - redirecting to signup');
          setAuthState("signup");
        }
      }).catch((error) => {
        console.error('Error fetching organizations:', error);
        // On API error, also redirect to signup for recovery
        setAuthState("signup");
      });
    } else {
      console.log('❌ NO USER - skipping API calls');
    }
  }, [user, setAuthState]);

  // isCheckingAuth runs before the router mounts, so route-specific Suspense fallbacks
  // never get a chance to show — resolve a matching skeleton by pathname instead.
  if (verifyingCheckout || isCheckingAuth) {
    if (isCheckingAuth && !verifyingCheckout) {
      if (isOnStudioRoute) {
        return <BootLoadingScreen text="Initializing Studio" />;
      }
      const isOnDashboardRoute = /\/integration\/[^/]+\/dashboard$/.test(window.location.pathname);
      if (window.location.pathname === '/' || isOnDashboardRoute) {
        return <BootLoadingScreen />;
      }
      return <RouteSkeleton pathname={window.location.pathname} />;
    }
    return (
      <Spinner color="var(--bs-secondary-color)" />
    )
  }

  return (authState === AuthState.SignedIn || authState === AuthState.Onboarding) && user ? (
    <>
      <ProtectedRoutes />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover={false}
      />
    </>
  ) : (
    <div className="container-fluid position-relative" style={{ minHeight: '100vh' }}>
      <style>{AUTH_STYLES}</style>

      <div className="row flex-column flex-lg-row" style={{ minHeight: "100svh" }}>
        {/* Dark sidebar — hidden on mobile */}
        <div className="col-lg-5 d-none d-lg-flex flex-column" style={{ background: '#111827', position: 'relative', overflow: 'hidden', padding: '36px 44px' }}>
          {/* Decorative blobs */}
          <div style={{ position: 'absolute', bottom: -80, left: -80, width: 320, height: 320, borderRadius: '50%', background: 'rgba(37,99,235,.09)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: 60, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(124,58,237,.07)', pointerEvents: 'none' }} />
          {/* Waveform watermark */}
          <div style={{ position: 'absolute', bottom: 40, right: 30, opacity: .07 }}>
            <svg width="160" height="60" viewBox="0 0 160 60" fill="white">
              {Array.from({ length: 26 }, (_, i) => {
                const h = 10 + Math.abs(Math.sin(i * .7) * 22 + Math.cos(i * .4) * 12);
                return <rect key={i} x={i * 6} y={(60 - h) / 2} width={4} height={h} rx={2} />;
              })}
            </svg>
          </div>

          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 36 }}>
            <img className="logo" src={logo} alt="logo" style={{ height: 28 }} />
          </div>

          {/* Illustration area */}
          <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 28 }}>
            {/* Pulse rings */}
            {[200, 150, 100].map((s, i) => (
              <div key={i} style={{
                position: 'absolute', width: s, height: s, borderRadius: '50%',
                border: `1px solid rgba(37,99,235,${0.12 + i * 0.08})`,
                animation: `pulse ${2.5 + i * 0.5}s ease-in-out infinite`,
                top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
              }} />
            ))}

            {/* Mic orb */}
            <div style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg,rgba(37,99,235,.5),rgba(124,58,237,.5))', border: '1px solid rgba(99,153,255,.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 0 40px rgba(37,99,235,.3)' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="rgba(191,219,254,1)" strokeWidth="1.5" strokeLinecap="round">
                  <rect x="9" y="2" width="6" height="13" rx="3" />
                  <path d="M5 10a7 7 0 0 0 14 0M12 19v3M8 22h8" />
                </svg>
              </div>
              {/* Animated waveform bars */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, height: 52 }}>
                {Array.from({ length: 24 }, (_, i) => (
                  <div key={i} style={{
                    width: 3, borderRadius: 2,
                    background: 'linear-gradient(to top,rgba(37,99,235,.9),rgba(147,197,253,.5))',
                    animationName: 'waveBar',
                    animationDuration: `${0.6 + ((i * 7) % 5) * 0.18}s`,
                    animationTimingFunction: 'ease-in-out',
                    animationIterationCount: 'infinite',
                    animationDirection: 'alternate',
                    animationDelay: `${(i * 31) % 400}ms`,
                    height: `${18 + Math.abs(Math.sin(i * 0.65) * 26)}px`,
                  }} />
                ))}
              </div>
            </div>

            {/* Active Voice card */}
            <div style={{ position: 'absolute', top: '8%', right: '-8px', background: 'rgba(15,23,42,.85)', backdropFilter: 'blur(12px)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(255,255,255,.1)', boxShadow: '0 4px 24px rgba(0,0,0,.3)', minWidth: 140 }}>
              <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Active Voice</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'linear-gradient(135deg,#1e3a8a,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'white', flexShrink: 0 }}>J</div>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: 'white', lineHeight: 1.1 }}>Joanna</div>
                  <div style={{ fontSize: 10, color: 'rgba(147,197,253,.8)' }}>Emotional · EN</div>
                </div>
              </div>
            </div>

            {/* Monthly Usage card */}
            <div style={{ position: 'absolute', bottom: '10%', left: '-12px', background: 'rgba(15,23,42,.85)', backdropFilter: 'blur(12px)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(255,255,255,.1)', boxShadow: '0 4px 24px rgba(0,0,0,.3)' }}>
              <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Monthly Usage</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'white', lineHeight: 1.1 }}>48,662</div>
              <div style={{ fontSize: 10, color: 'rgba(74,222,128,.85)', marginTop: 2 }}>↑ +2,340 today</div>
            </div>

            {/* Generating badge */}
            <div style={{ position: 'absolute', top: '52%', right: '-16px', background: 'rgba(37,99,235,.25)', backdropFilter: 'blur(12px)', borderRadius: 8, padding: '8px 12px', border: '1px solid rgba(99,153,255,.3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 6px #4ade80' }} />
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.8)', fontWeight: 600 }}>Generating…</span>
              </div>
            </div>

            {/* Floating user name pills */}
            {['Sophie Park', 'Darius W.', 'Naomi C.'].map((name, i) => (
              <div key={i} style={{
                position: 'absolute', top: `${22 + i * 26}%`, left: i % 2 === 0 ? '0%' : '-6px',
                background: 'rgba(255,255,255,.07)', borderRadius: 999, padding: '3px 10px',
                fontSize: 10.5, color: 'rgba(255,255,255,.55)', fontWeight: 500,
                border: '1px solid rgba(255,255,255,.1)', whiteSpace: 'nowrap',
                animation: `float ${2 + i * 0.4}s ease-in-out infinite alternate`,
              }}>{name}</div>
            ))}
          </div>

          {/* Headline + feature list */}
          <div style={{ marginBottom: 40 }}>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: 'white', lineHeight: 1.25, marginBottom: 22, letterSpacing: '-.02em' }}>
              Everything you need to make the perfect voiceover
            </h1>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {FEATURES.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="8" height="7" viewBox="0 0 9 7" fill="none">
                      <path d="M1 3.5L3.2 6 8 1" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', fontWeight: 500 }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile logo row — shown only on mobile */}
        <div className="col-12 d-flex d-lg-none flex-column justify-content-between px-4 py-4">
          <div className="row">
            <div className="col-12">
              <div className="d-flex align-items-center">
                <img className="logo me-1" src={logoBlack} alt="logo" style={{ height: 28 }} />
              </div>
            </div>
          </div>
        </div>

        {/* Main form column */}
        <div className="col-lg-7 d-flex flex-column flex-grow-1 justify-content-center px-4 px-lg-5">
          <BrowserRouter>
            <PageViewTracker />
            <Switch>
              <Route exact path="/google/callback">
                <GoogleCallback />
              </Route>
              <Route path="*">
                {authState === AuthState.SignIn && (
                  <SignIn />
                )}
                {authState === AuthState.SignUp && (
                  <SignUp />
                )}
                {authState === AuthState.ForgotPassword && (
                  <ForgotPassword />
                )}
              </Route>
            </Switch>
          </BrowserRouter>

        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover={false}
      />
    </div>
  );
}

export default App;
